import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import Loading from "./elements/Loading";
import { fetchCustomerById, updateCustomer } from "./utils/api/api";
import { MdOutlinePhotoCamera } from "react-icons/md";
import Layout from "./elements/Layout";
import GlassInput from "./components/GlassInput";
import GlassSection from "./components/GlassSection";

const EditInfo = () => {
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("personal");
  const [previewImage, setPreviewImage] = useState(null);
  const customerId = Cookies.get("user_id");
  const [editCustomerData, setEditCustomerData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    id_number: "",
    dob: "",
    gender: "",
    nationality: "",
    city: "",
    state: "",
    country: "",
    next_of_kin1_name: "",
    next_of_kin1_phone: "",
    next_of_kin1_email: "",
    next_of_kin1_idNo: "",
    next_of_kin1_relationship: "",
    next_of_kin2_name: "",
    next_of_kin2_phone: "",
    next_of_kin2_email: "",
    next_of_kin2_idNo: "",
    next_of_kin2_relationship: "",
    photo: "",
    is_group: false,
    participants: "",
  });

  useEffect(() => {
    const getCustomer = async () => {
      try {
        const data = await fetchCustomerById(customerId);
        setEditCustomerData({ ...data.customer, photo: null });
        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    getCustomer();
  }, [customerId]);

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditCustomerData({
      ...editCustomerData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setEditCustomerData((prevData) => ({
      ...prevData,
      photo: file || null,
    }));
  };

  const handleCustomerFormSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    const fieldsToSend = [
      "id",
      "email",
      "first_name",
      "last_name",
      "phone_number",
      "id_number",
      "dob",
      "gender",
      "nationality",
      "city",
      "state",
      "country",
      "next_of_kin1_name",
      "next_of_kin1_phone",
      "next_of_kin1_email",
      "next_of_kin1_idNo",
      "next_of_kin1_relationship",
      "next_of_kin2_name",
      "next_of_kin2_phone",
      "next_of_kin2_email",
      "next_of_kin2_idNo",
      "next_of_kin2_relationship",
      "photo",
      "is_group",
      "participants",
    ];

    fieldsToSend.forEach((field) => {
      if (editCustomerData[field] !== undefined) {
        formData.append(field, editCustomerData[field]);
      }
    });

    setLoading(true);
    try {
      await updateCustomer(formData);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error(error?.response?.data?.message || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        {loading && <Loading />}

        <div className="relative max-w-3xl mx-auto px-4 py-8">
          <header className="mb-10">
            <h1 className="text-2xl font-medium text-slate-800">
              Edit Profile
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              Update your personal information
            </p>
          </header>

          {/* Profile Photo - Optional, simplified */}
          <div className="mb-8 flex items-center gap-4">
            <div className="relative group">
              <div className="w-20 h-20 rounded-full overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src={
                    previewImage ||
                    "https://imgs.search.brave.com/fbxInw05M6mkNEHaT64Qm3dNEzTIVXrwCD4lxk4ve3A/rs:fit:500:0:0:0/g:ce/aHR0cHM6Ly90NC5m/dGNkbi5uZXQvanBn/LzAyLzE3LzM0LzY3/LzM2MF9GXzIxNzM0/Njc4Ml83WHBDVHQ4/YkxOSnF2VkFhRFpK/d3Zaam0wZXBRbWo2/ai5qcGc"
                  }
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity rounded-full cursor-pointer">
                <MdOutlinePhotoCamera size={20} className="text-white" />
                <input
                  type="file"
                  name="photo"
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </label>
            </div>
            <div>
              <p className="text-slate-700 font-medium">
                {editCustomerData.first_name} {editCustomerData.last_name}
              </p>
              <p className="text-slate-500 text-sm">{editCustomerData.email}</p>
            </div>
          </div>

          {/* Section Tabs - Simplified */}
          <div className="flex mb-8 gap-3 overflow-x-auto no-scrollbar">
            {["personal", "address", "nextofkin1", "nextofkin2", "account"].map(
              (section) => (
                <button
                  key={section}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                    activeSection === section
                      ? "bg-slate-800 text-white"
                      : "bg-white text-slate-600 hover:bg-slate-100"
                  }`}
                  onClick={() => setActiveSection(section)}
                >
                  {
                    {
                      personal: "Personal",
                      address: "Location",
                      nextofkin1: "Primary Contact",
                      nextofkin2: "Secondary Contact",
                      account: "Account",
                    }[section]
                  }
                </button>
              )
            )}
          </div>

          <form onSubmit={handleCustomerFormSubmit}>
            {/* Personal Details Section */}
            <div
              className={`${
                activeSection === "personal" ? "block" : "hidden"
              } bg-white p-6 rounded-lg shadow-sm mb-6`}
            >
              <h2 className="text-lg font-medium text-slate-800 mb-5">
                Personal Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={editCustomerData.first_name}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={editCustomerData.last_name}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={editCustomerData.email}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone_number"
                    value={editCustomerData.phone_number}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ID Number
                  </label>
                  <input
                    type="text"
                    name="id_number"
                    value={editCustomerData.id_number}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={editCustomerData.dob}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md bg-slate-50 text-slate-500 cursor-not-allowed"
                    disabled
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Date of birth cannot be changed
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={editCustomerData.gender}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  >
                    <option value="" disabled>
                      Select gender
                    </option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Location Section */}
            <div
              className={`${
                activeSection === "address" ? "block" : "hidden"
              } bg-white p-6 rounded-lg shadow-sm mb-6`}
            >
              <h2 className="text-lg font-medium text-slate-800 mb-5">
                Location Information
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Nationality
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={editCustomerData.nationality}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={editCustomerData.country}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={editCustomerData.city}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    State/Province
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={editCustomerData.state}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Primary Contact Section */}
            <div
              className={`${
                activeSection === "nextofkin1" ? "block" : "hidden"
              } bg-white p-6 rounded-lg shadow-sm mb-6`}
            >
              <h2 className="text-lg font-medium text-slate-800 mb-5">
                Primary Emergency Contact
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="next_of_kin1_name"
                  value={editCustomerData.next_of_kin1_name}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="next_of_kin1_phone"
                  value={editCustomerData.next_of_kin1_phone}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  required
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="next_of_kin1_email"
                  value={editCustomerData.next_of_kin1_email}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ID Number
                  </label>
                  <input
                    type="text"
                    name="next_of_kin1_idNo"
                    value={editCustomerData.next_of_kin1_idNo}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Relationship
                  </label>
                  <input
                    type="text"
                    name="next_of_kin1_relationship"
                    value={editCustomerData.next_of_kin1_relationship}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* Secondary Contact Section */}
            <div
              className={`${
                activeSection === "nextofkin2" ? "block" : "hidden"
              } bg-white p-6 rounded-lg shadow-sm mb-6`}
            >
              <h2 className="text-lg font-medium text-slate-800 mb-5">
                Secondary Emergency Contact
              </h2>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  name="next_of_kin2_name"
                  value={editCustomerData.next_of_kin2_name}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="text"
                  name="next_of_kin2_phone"
                  value={editCustomerData.next_of_kin2_phone}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  name="next_of_kin2_email"
                  value={editCustomerData.next_of_kin2_email}
                  onChange={handleEditInputChange}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    ID Number
                  </label>
                  <input
                    type="text"
                    name="next_of_kin2_idNo"
                    value={editCustomerData.next_of_kin2_idNo}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Relationship
                  </label>
                  <input
                    type="text"
                    name="next_of_kin2_relationship"
                    value={editCustomerData.next_of_kin2_relationship}
                    onChange={handleEditInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  />
                </div>
              </div>
            </div>

            {/* Account Settings Section */}
            <div
              className={`${
                activeSection === "account" ? "block" : "hidden"
              } bg-white p-6 rounded-lg shadow-sm mb-6`}
            >
              <h2 className="text-lg font-medium text-slate-800 mb-5">
                Account Settings
              </h2>
              
              <div className="mb-6">
                <label className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="is_group"
                    checked={editCustomerData.is_group}
                    onChange={handleEditInputChange}
                    className="w-4 h-4 text-slate-800 border-slate-300 rounded focus:ring-slate-800"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Group Account
                  </span>
                </label>
                <p className="text-xs text-slate-500 mt-1 ml-7">
                  Enable this option for business or group accounts
                </p>
              </div>
              
              {editCustomerData.is_group && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Participants
                  </label>
                  <textarea
                    name="participants"
                    value={editCustomerData.participants}
                    onChange={handleEditInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800"
                  ></textarea>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center mt-10">
              <button
                type="button"
                onClick={() => window.history.back()}
                className="px-5 py-2 rounded-md text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 rounded-md text-white bg-slate-800 hover:bg-slate-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default EditInfo;